import { create } from 'zustand';
import type { AffiliateCommissionDto, AffiliateDto, AffiliateInput, AffiliatePayoutDto, AffiliatePayoutRequest, AffiliateStatus } from '@ecom/contracts';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';

/**
 * Affiliates (ADR-084) — searched and paged on the server; one open with its
 * commissions and payouts; added, changed and paid here.
 */

interface AffiliatesState {
  items: AffiliateDto[];
  nextCursor: string | null;
  total: number | null;
  search: string;
  status: AffiliateStatus | '';
  loading: boolean;
  error: string | null;

  current: AffiliateDto | null;
  commissions: AffiliateCommissionDto[];
  commissionsCursor: string | null;
  payouts: AffiliatePayoutDto[];
  saving: boolean;
  saveError: string | null;

  load: (filters?: { search?: string; status?: AffiliateStatus | '' }) => Promise<void>;
  loadMore: () => Promise<void>;
  open: (id: string) => Promise<void>;
  close: () => void;
  moreCommissions: () => Promise<void>;
  save: (id: string | null, input: AffiliateInput) => Promise<AffiliateDto | null>;
  pay: (id: string, input: AffiliatePayoutRequest) => Promise<boolean>;
  clearSaveError: () => void;
}

const PAGE_SIZE = 20;

export const useAffiliatesStore = create<AffiliatesState>((set, get) => ({
  items: [],
  nextCursor: null,
  total: null,
  search: '',
  status: '',
  loading: false,
  error: null,
  current: null,
  commissions: [],
  commissionsCursor: null,
  payouts: [],
  saving: false,
  saveError: null,

  async load(filters = {}) {
    const search = filters.search ?? get().search;
    const status = filters.status ?? get().status;
    set({ loading: true, error: null, search, status });
    try {
      const page = await withSession(() =>
        adminApi.affiliates.list({ limit: PAGE_SIZE, ...(search.trim() ? { search: search.trim() } : {}), ...(status ? { status } : {}) }),
      );
      // A newer search answered first: this one is stale.
      if (get().search !== search || get().status !== status) return;
      set({ items: page.items, nextCursor: page.meta.nextCursor, total: page.meta.total ?? null, loading: false });
    } catch (error) {
      set({ loading: false, error: errorMessage(error, 'Could not load the affiliates.') });
    }
  },

  async loadMore() {
    const { nextCursor, search, status, loading } = get();
    if (!nextCursor || loading) return;
    set({ loading: true });
    try {
      const page = await withSession(() =>
        adminApi.affiliates.list({ limit: PAGE_SIZE, cursor: nextCursor, ...(search.trim() ? { search: search.trim() } : {}), ...(status ? { status } : {}) }),
      );
      set((state) => ({ items: [...state.items, ...page.items], nextCursor: page.meta.nextCursor, loading: false }));
    } catch (error) {
      set({ loading: false, error: errorMessage(error, 'Could not load more affiliates.') });
    }
  },

  async open(id) {
    set({ saveError: null, commissions: [], commissionsCursor: null, payouts: [] });
    try {
      const [current, commissions, payouts] = await Promise.all([
        withSession(() => adminApi.affiliates.get(id)),
        withSession(() => adminApi.affiliates.commissions(id, { limit: PAGE_SIZE })),
        withSession(() => adminApi.affiliates.payouts(id)),
      ]);
      set({ current, commissions: commissions.items, commissionsCursor: commissions.meta.nextCursor, payouts: payouts.items });
    } catch (error) {
      set({ saveError: errorMessage(error, 'Could not load the affiliate.') });
    }
  },

  close() {
    set({ current: null, commissions: [], commissionsCursor: null, payouts: [], saveError: null });
  },

  async moreCommissions() {
    const { current, commissionsCursor } = get();
    if (!current || !commissionsCursor) return;
    try {
      const page = await withSession(() => adminApi.affiliates.commissions(current.id, { limit: PAGE_SIZE, cursor: commissionsCursor }));
      set((state) => ({ commissions: [...state.commissions, ...page.items], commissionsCursor: page.meta.nextCursor }));
    } catch (error) {
      set({ saveError: errorMessage(error, 'Could not load more commissions.') });
    }
  },

  async save(id, input) {
    set({ saving: true, saveError: null });
    try {
      const saved = await withSession(() => (id ? adminApi.affiliates.update(id, input) : adminApi.affiliates.create(input)));
      set((state) => ({
        saving: false,
        items: id ? state.items.map((item) => (item.id === id ? saved : item)) : [saved, ...state.items],
        total: id || state.total === null ? state.total : state.total + 1,
        current: state.current?.id === saved.id ? saved : state.current,
      }));
      return saved;
    } catch (error) {
      set({ saving: false, saveError: errorMessage(error, 'Could not save the affiliate.') });
      return null;
    }
  },

  async pay(id, input) {
    set({ saving: true, saveError: null });
    try {
      await withSession(() => adminApi.affiliates.pay(id, input));
      set({ saving: false });
      await get().open(id);
      void get().load();
      return true;
    } catch (error) {
      set({ saving: false, saveError: errorMessage(error, 'Could not record the payout.') });
      return false;
    }
  },

  clearSaveError() {
    set({ saveError: null });
  },
}));
